import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { hallGpuQuality, isAppleTouchDevice } from "../bingo-shared/display";
import {
  playCallDing,
  playDropWhoosh,
  playMixRumble,
  playThud,
  stopMixRumble,
} from "./audio";
import { cellTarget } from "./cells";
import { COLUMN_COLORS, columnFor, fisherYates } from "./logic";
import { useHallStore } from "./store";
import { disposeTextures, getBallMaterial, getFaceMaterial } from "./textures";
import { hallTheme } from "./themes";
import { PHASE_DUR, phaseTime } from "./timeline";

const _v = new THREE.Vector3();
const _n = new THREE.Vector3();
const _from = new THREE.Vector3();
const _to = new THREE.Vector3();
const _cagePos = new THREE.Vector3();
const _dlt = new THREE.Vector3();
const _rel = new THREE.Vector3();
const _kick = new THREE.Vector3();

const HERO_GEO = new THREE.SphereGeometry(0.2, 48, 32);
const MIX_R = 0.168;
const MIX_GEO = new THREE.SphereGeometry(MIX_R, 20, 16);
const FACE_GEO = new THREE.CircleGeometry(0.162, 64);
const RING_GEO = new THREE.RingGeometry(0.24, 0.34, 48);
const RIM_GEO = new THREE.TorusGeometry(0.168, 0.014, 8, 48);
const FOV_RAD = (32 * Math.PI) / 180;
const CAGE_RADIUS = 0.92;

function easeOutBack(t: number) {
  const c = 1.70158;
  const u = t - 1;
  return 1 + c * u * u * u + u * u;
}
function easeInCubic(t: number) {
  return t * t * t;
}
function easeInOut(t: number) {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

function screenToWorld(
  cx: number,
  cy: number,
  planeZ: number,
  camera: THREE.Camera,
  canvas: HTMLCanvasElement,
  out: THREE.Vector3,
) {
  const rect = canvas.getBoundingClientRect();
  const ndcX = ((cx - rect.left) / Math.max(1, rect.width)) * 2 - 1;
  const ndcY = -((cy - rect.top) / Math.max(1, rect.height)) * 2 + 1;
  _v.set(ndcX, ndcY, 0.5).unproject(camera);
  _v.sub(camera.position).normalize();
  const denom = _v.z;
  const t = Math.abs(denom) < 1e-5 ? 0 : (planeZ - camera.position.z) / denom;
  out.copy(camera.position).addScaledVector(_v, t);
  return out;
}

function SceneInner({ quality }: { quality: "high" | "low" }) {
  useEffect(() => () => disposeTextures(), []);
  return (
    <>
      <CameraRig />
      <Lights />
      <Cage quality={quality} />
      <HeroBall compact={quality === "low"} />
    </>
  );
}

function CameraRig() {
  const { camera, gl } = useThree();
  useFrame((_, dt) => {
    const d = Math.min(dt, 0.1);
    const store = useHallStore.getState();
    store.decayTrauma(d);
    const t = store.trauma;
    const now = performance.now() / 1000;
    const rect = gl.domElement.getBoundingClientRect();
    const persp = camera as THREE.PerspectiveCamera;
    if (persp.isPerspectiveCamera) {
      const aspect = rect.width / Math.max(1, rect.height);
      if (Math.abs(persp.aspect - aspect) > 0.01) {
        persp.aspect = aspect;
        persp.updateProjectionMatrix();
      }
    }
    camera.position.x = Math.sin(now * 58) * t * 0.08;
    camera.position.y = Math.cos(now * 47) * t * 0.06;
    camera.position.z = 6.4;
    camera.lookAt(0, 0, 0);
  });
  return null;
}

function Lights() {
  const phase = useHallStore((s) => s.phase);
  const themeId = useHallStore((s) => s.theme);
  const theme = hallTheme(themeId);
  const calling = phase === "calling" || phase === "dropping";
  return (
    <>
      <ambientLight intensity={0.78} color={theme.ambient} />
      <hemisphereLight args={[theme.hemiSky, theme.hemiGround, 0.85]} />
      <directionalLight position={[2.4, 4.2, 6]} intensity={1.7} color={theme.dir} />
      <pointLight position={[0, 0.2, 3.4]} intensity={calling ? 4.2 : 0.35} color={theme.callLight} distance={8} />
      <pointLight position={[0, 0.4, 2.4]} intensity={phase === "mixing" ? 3.8 : 0.2} color={theme.mixLight} distance={5} />
    </>
  );
}

function Cage({ quality }: { quality: "high" | "low" }) {
  const group = useRef<THREE.Group>(null);
  const spin = useRef<THREE.Group>(null);
  const liveRef = useRef(false);
  const { camera, gl, invalidate } = useThree();
  const themeId = useHallStore((s) => s.theme);
  const theme = hallTheme(themeId);
  const mixKey = useHallStore((s) => `${s.current ?? 0}-${s.called.length}`);

  useFrame((_, dt) => {
    const d = Math.min(dt, 0.1);
    const g = group.current;
    if (!g) return;
    const phase = useHallStore.getState().phase;
    const mix = phase === "mixing";
    const dropping = phase === "dropping";
    const live = mix || dropping;
    const canvas = gl.domElement;
    const rect = canvas.getBoundingClientRect();
    const planeZ = live ? 2.25 : -0.6;
    screenToWorld(rect.left + rect.width * 0.5, rect.top + rect.height * 0.5, planeZ, camera, canvas, _to);
    const dist = Math.abs(camera.position.z - planeZ);
    const worldH = 2 * Math.tan(FOV_RAD / 2) * Math.max(0.2, dist);
    const spanPx = Math.min(rect.width, rect.height);
    const spanWorld = (spanPx / Math.max(1, rect.height)) * worldH;
    const tsTarget = live ? THREE.MathUtils.clamp((spanWorld * 0.8) / (CAGE_RADIUS * 2), 0.48, 1.15) : 0.001;
    const appearing = live && !liveRef.current;
    liveRef.current = live;
    if (appearing) {
      g.position.copy(_to);
      g.scale.setScalar(tsTarget);
      invalidate();
    } else {
      const k = mix ? 10.5 : 9.4;
      const ease = 1 - Math.exp(-k * d);
      g.position.x += (_to.x - g.position.x) * ease;
      g.position.y += (_to.y - g.position.y) * ease;
      g.position.z += (_to.z - g.position.z) * ease;
      g.scale.setScalar(g.scale.x + (tsTarget - g.scale.x) * ease);
    }
    if (spin.current) {
      spin.current.rotation.y += (mix ? 3.15 : 0.55) * d;
      spin.current.rotation.z += (mix ? 1.35 : 0.16) * d;
      spin.current.rotation.x = Math.sin(performance.now() / 780) * (mix ? 0.34 : 0.08);
    }
    _cagePos.copy(g.position);
  });

  const rings: [number, number, number][] = [
    [Math.PI / 2, 0, 0],
    [0, 0, 0],
    [0, 0, Math.PI / 2],
    [0.72, 0.38, 0.18],
    [-0.58, 0.82, 0.12],
    [0.32, -0.64, 0.55],
  ];
  const segs = quality === "high" ? 32 : 18;
  const ballCount = quality === "high" ? 20 : 14;

  return (
    <group ref={group} position={[0, 0, 2.25]} scale={0.001}>
      <group ref={spin}>
        <mesh>
          <sphereGeometry args={[CAGE_RADIUS, segs, Math.max(14, segs - 8)]} />
          <meshStandardMaterial
            color={theme.glass}
            transparent
            opacity={quality === "high" ? 0.07 : 0.05}
            roughness={0.12}
            metalness={0.08}
            depthWrite={false}
            side={THREE.DoubleSide}
          />
        </mesh>
        {rings.map((rot, i) => (
          <mesh key={i} rotation={rot}>
            <torusGeometry args={[CAGE_RADIUS, 0.02, 8, 40]} />
            <meshStandardMaterial color={theme.cageMetal} metalness={1} roughness={0.22} />
          </mesh>
        ))}
      </group>
      <MixerBalls key={mixKey} radius={CAGE_RADIUS} count={ballCount} />
    </group>
  );
}

type Mixer = {
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  num: number;
  seed: number;
};

function MixerBalls({ radius, count }: { radius: number; count: number }) {
  const meshRefs = useRef<(THREE.Mesh | null)[]>([]);
  const remaining = useHallStore((s) => s.remaining);
  const current = useHallStore((s) => s.current);
  const balls = useMemo<Mixer[]>(() => {
    const pool = remaining.slice();
    if (current != null && !pool.includes(current)) pool.push(current);
    const nums = fisherYates(pool).slice(0, count);
    const inner = radius - MIX_R - 0.04;
    const golden = Math.PI * (3 - Math.sqrt(5));
    return nums.map((num, i) => {
      const y = count <= 1 ? 0 : 1 - (i / (count - 1)) * 2;
      const r = Math.sqrt(Math.max(0, 1 - y * y));
      const theta = golden * i;
      const spread = inner * 0.72;
      return {
        pos: new THREE.Vector3(Math.cos(theta) * r * spread, y * spread, Math.sin(theta) * r * spread),
        vel: new THREE.Vector3().randomDirection().multiplyScalar(3.4 + Math.random() * 2.2),
        num,
        seed: i * 17.13 + num * 0.37,
      };
    });
  }, [radius, count, remaining, current]);

  useFrame((_, dt) => {
    const d = Math.min(dt, 0.033);
    const mixing = useHallStore.getState().phase === "mixing";
    const inner = radius - MIX_R - 0.02;
    const minDist = MIX_R * 2;
    const t = performance.now() / 1000;
    const swirl = mixing ? 5.8 : 1.6;
    const kick = mixing ? 16 : 4.2;

    for (let i = 0; i < balls.length; i++) {
      const b = balls[i];
      if (!b) continue;
      b.vel.x += -b.pos.z * swirl * d;
      b.vel.z += b.pos.x * swirl * d;
      b.vel.y += Math.sin(t * 8.4 + b.seed) * kick * d;
      b.vel.x += Math.cos(t * 7.1 + b.seed * 0.7) * kick * 0.65 * d;
      b.vel.z += Math.sin(t * 6.2 + b.seed * 1.3) * kick * 0.65 * d;
      if (mixing && (i + Math.floor(t * 18)) % 7 === 0) {
        _kick.randomDirection();
        b.vel.addScaledVector(_kick, 2.8 * d * 60);
      }
      const speed = b.vel.length();
      if (speed > 7.2) b.vel.multiplyScalar(7.2 / speed);
      b.vel.multiplyScalar(mixing ? 0.994 : 0.97);
      b.pos.addScaledVector(b.vel, d);

      const len = b.pos.length();
      if (len > inner) {
        _n.copy(b.pos).normalize();
        b.pos.copy(_n).multiplyScalar(inner);
        const vn = b.vel.dot(_n);
        if (vn > 0) b.vel.addScaledVector(_n, -1.92 * vn);
        b.vel.addScaledVector(_n, -0.15);
        _kick.set(-_n.y, _n.x, _n.z).normalize();
        b.vel.addScaledVector(_kick, 1.1);
      }
    }

    for (let i = 0; i < balls.length; i++) {
      const a = balls[i];
      if (!a) continue;
      for (let j = i + 1; j < balls.length; j++) {
        const b = balls[j];
        if (!b) continue;
        _dlt.subVectors(a.pos, b.pos);
        const dist = _dlt.length();
        if (dist >= minDist || dist < 1e-4) continue;
        _n.copy(_dlt).multiplyScalar(1 / dist);
        const overlap = minDist - dist;
        a.pos.addScaledVector(_n, overlap * 0.5);
        b.pos.addScaledVector(_n, -overlap * 0.5);
        _rel.subVectors(a.vel, b.vel);
        const vn = _rel.dot(_n);
        if (vn < 0) {
          a.vel.addScaledVector(_n, -vn * 1.05);
          b.vel.addScaledVector(_n, vn * 1.05);
        }
      }
    }

    for (let i = 0; i < balls.length; i++) {
      const b = balls[i];
      const mesh = meshRefs.current[i];
      if (!b || !mesh) continue;
      mesh.position.copy(b.pos);
      mesh.rotation.x += b.vel.y * d * 4.4;
      mesh.rotation.z += b.vel.x * d * 4.4;
      mesh.rotation.y += b.vel.z * d * 2.2;
    }
  });

  return (
    <>
      {balls.map((b, i) => (
        <mesh
          key={b.num}
          ref={(el) => {
            meshRefs.current[i] = el;
          }}
          geometry={MIX_GEO}
          material={getBallMaterial(b.num)}
          position={b.pos}
        />
      ))}
    </>
  );
}

function HeroBall({ compact }: { compact: boolean }) {
  const group = useRef<THREE.Group>(null);
  const glow = useRef<THREE.Mesh>(null);
  const faceMesh = useRef<THREE.Mesh>(null);
  const current = useHallStore((s) => s.current);
  const bounced = useRef(false);
  const lastPhase = useRef("idle");
  const callScale = compact ? 2.45 : 2.95;
  const popPos = useRef(new THREE.Vector3(0, 0, 3.1));
  const { camera, gl } = useThree();
  const material = current ? getBallMaterial(current) : null;
  const face = current ? getFaceMaterial(current) : null;
  const accent = current ? COLUMN_COLORS[columnFor(current)] : "#f4ead8";

  useEffect(() => {
    let prev = useHallStore.getState().phase;
    return useHallStore.subscribe((s) => {
      if (s.phase === prev) return;
      prev = s.phase;
      if (s.phase === "mixing" && s.soundEnabled) playMixRumble();
      else stopMixRumble();
      if (s.phase === "dropping" && s.soundEnabled) playDropWhoosh();
      if (s.phase === "calling" && s.soundEnabled) playCallDing();
    });
  }, []);

  useFrame((_, dt) => {
    const d = Math.min(dt, 0.1);
    const store = useHallStore.getState();
    const { phase, current: num, soundEnabled } = store;
    if (lastPhase.current !== phase) {
      bounced.current = false;
      lastPhase.current = phase;
    }
    const elapsed = phaseTime.phase === phase ? phaseTime.t : 0;
    const g = group.current;
    if (!g || num == null) {
      if (g) g.visible = false;
      return;
    }

    if (phase === "mixing" || phase === "idle" || phase === "celebrating") {
      g.visible = false;
      return;
    }

    g.visible = true;
    const showFace = phase !== "dropping" || elapsed / PHASE_DUR.dropping > 0.78;
    if (faceMesh.current) faceMesh.current.visible = showFace;
    if (glow.current) {
      const mat = glow.current.material as THREE.MeshBasicMaterial;
      const show = phase === "calling" || (phase === "dropping" && elapsed / PHASE_DUR.dropping > 0.55);
      mat.opacity = show ? 0.5 + Math.sin(elapsed * 4.2) * 0.12 : 0;
      glow.current.visible = mat.opacity > 0.02;
    }

    if (phase === "dropping") {
      const u = Math.min(1, elapsed / PHASE_DUR.dropping);
      const e = easeOutBack(Math.min(1, u));
      _from.copy(_cagePos);
      const rect = gl.domElement.getBoundingClientRect();
      screenToWorld(rect.left + rect.width * 0.5, rect.top + rect.height * 0.5, 3.1, camera, gl.domElement, _to);
      g.position.lerpVectors(_from, _to, e);
      g.scale.setScalar(0.42 + e * (callScale - 0.42));
      g.rotation.x += d * 6.2;
      g.rotation.z += d * 3.8;
      if (!bounced.current && u > 0.7) {
        bounced.current = true;
        store.addTrauma(0.48);
        if (soundEnabled) playThud();
      }
      if (u > 0.78) {
        g.lookAt(camera.position);
      }
      popPos.current.copy(g.position);
      return;
    }

    if (phase === "calling") {
      const rect = gl.domElement.getBoundingClientRect();
      screenToWorld(rect.left + rect.width * 0.5, rect.top + rect.height * 0.5, 3.1, camera, gl.domElement, _to);
      g.position.set(_to.x, _to.y + Math.sin(elapsed * 2.3) * 0.045, _to.z);
      g.scale.setScalar(callScale + Math.sin(elapsed * 3.0) * 0.05);
      g.lookAt(camera.position);
      popPos.current.copy(g.position);
      return;
    }

    if (phase === "seating") {
      const u = Math.min(1, elapsed / PHASE_DUR.seating);
      const fallU = Math.max(0, (u - 0.06) / 0.94);
      const e = easeInOut(fallU);
      const fall = easeInCubic(fallU);
      _from.copy(popPos.current);
      const dest = cellTarget(num);
      if (dest) {
        screenToWorld(dest.x, dest.y, 0.72, camera, gl.domElement, _to);
        const rect = gl.domElement.getBoundingClientRect();
        const dist = Math.abs(camera.position.z - 0.72);
        const worldH = 2 * Math.tan(FOV_RAD / 2) * dist;
        const wellWorld = (dest.size / Math.max(1, rect.height)) * worldH;
        const landScale = THREE.MathUtils.clamp((wellWorld * 0.92) / 0.4, 0.14, 0.58);
        g.position.lerpVectors(_from, _to, fall);
        g.position.y += Math.sin(fall * Math.PI) * 0.42;
        const bounce = fallU > 0.82 ? 1 + Math.sin(((fallU - 0.82) / 0.18) * Math.PI) * 0.16 : 1;
        const hide = fallU > 0.93 ? 1 - (fallU - 0.93) / 0.07 : 1;
        g.scale.setScalar(THREE.MathUtils.lerp(callScale, landScale, e) * bounce * hide);
        if (hide < 0.08) g.visible = false;
      } else {
        g.position.lerpVectors(_from, _to.set(0, -0.6, 1.1), e);
        g.scale.setScalar(THREE.MathUtils.lerp(callScale, 0.28, e));
      }
      g.lookAt(camera.position);
    }
  });

  if (!current || !material || !face) return null;

  return (
    <group ref={group} visible={false}>
      <mesh material={material} geometry={HERO_GEO} />
      <mesh ref={faceMesh} geometry={FACE_GEO} material={face} position={[0, 0, 0.21]} renderOrder={2} />
      <mesh geometry={RIM_GEO} position={[0, 0, 0.2]} renderOrder={2}>
        <meshStandardMaterial color={accent} metalness={0.28} roughness={0.32} />
      </mesh>
      <mesh ref={glow} geometry={RING_GEO} position={[0, 0, -0.02]} visible={false}>
        <meshBasicMaterial color={accent} transparent opacity={0} side={THREE.DoubleSide} depthWrite={false} />
      </mesh>
      <pointLight position={[0.25, 0.35, 0.55]} intensity={2.2} color={accent} distance={2.6} />
    </group>
  );
}

export function HallScene({ onLost }: { onLost?: () => void }) {
  const [quality] = useState<"high" | "low">(() => hallGpuQuality());
  const apple = isAppleTouchDevice();
  const phase = useHallStore((s) => s.phase);
  const [hidden, setHidden] = useState(
    () => typeof document !== "undefined" && document.visibilityState === "hidden",
  );

  useEffect(() => {
    const onVis = () => setHidden(document.visibilityState === "hidden");
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const live = phase === "mixing" || phase === "dropping" || phase === "calling" || phase === "seating";

  return (
    <Canvas
      className="absolute inset-0 h-full w-full"
      frameloop={hidden || !live ? "demand" : "always"}
      dpr={quality === "high" ? [1, 1.5] : [1, apple ? 1.25 : 1.35]}
      gl={{
        alpha: true,
        antialias: quality === "high",
        stencil: false,
        depth: true,
        powerPreference: apple ? "default" : "high-performance",
        premultipliedAlpha: true,
        failIfMajorPerformanceCaveat: false,
        toneMapping: THREE.ACESFilmicToneMapping,
        toneMappingExposure: 1.08,
      }}
      onCreated={({ gl }) => {
        gl.setClearColor(0x000000, 0);
        const canvas = gl.domElement;
        canvas.style.background = "transparent";
        canvas.style.display = "block";
        canvas.style.width = "100%";
        canvas.style.height = "100%";
        const lost = (event: Event) => {
          event.preventDefault();
          onLost?.();
        };
        canvas.addEventListener("webglcontextlost", lost, false);
      }}
      camera={{ fov: 32, near: 0.1, far: 40, position: [0, 0, 6.4] }}
      style={{
        touchAction: "none",
        pointerEvents: "none",
        width: "100%",
        height: "100%",
        background: "transparent",
        display: "block",
        transform: "translateZ(0)",
      }}
    >
      <SceneInner quality={quality} />
    </Canvas>
  );
}
